import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Search,
  Upload,
  FileSpreadsheet,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  Globe,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Minus,
  TrendingDown,
  TrendingUp,
  Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MediaSearchTab = () => {
  const { language } = useLanguage();
  
  // State
  const [textInput, setTextInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  // Compare state
  const [comparing, setComparing] = useState(false);
  const [compareResults, setCompareResults] = useState(null);
  const [selectedCompare, setSelectedCompare] = useState(new Set());
  const [applyingCompare, setApplyingCompare] = useState(false);
  const [compareOwnerEmail, setCompareOwnerEmail] = useState('');
  const [changeOwner, setChangeOwner] = useState(true);
  const [selectedDelete, setSelectedDelete] = useState(new Set());
  const [deletingProducts, setDeletingProducts] = useState(false);
  const compareFileRef = useRef(null);

  // Sensitive-conflict modal (when provider Excel only has regular PC but
  // the app already had premium pricing for that domain)
  const [sensitiveModal, setSensitiveModal] = useState(null);
  // shape: { conflicts: [{domain, local_pcp, local_pp}, ...] }

  const t = language === 'es' ? {
    title: 'Búsqueda Masiva de Medios',
    description: 'Busca si los dominios están en tu catálogo. Pega URLs o sube un archivo.',
    textTab: 'Pegar Texto',
    fileTab: 'Subir Archivo',
    textPlaceholder: 'Pega las URLs aquí (una por línea):\n\nhttps://ejemplo.com\nwww.otrodominio.es\nmidominio.com',
    search: 'Buscar',
    searching: 'Buscando...',
    uploadFile: 'Subir Excel/CSV',
    selectFile: 'Seleccionar archivo...',
    results: 'Resultados',
    found: 'Encontrados',
    notFound: 'No Encontrados',
    totalSearched: 'Total Buscados',
    downloadExcel: 'Descargar Excel Completo',
    domain: 'Dominio',
    price: 'Precio Compra',
    pvp: 'PVP',
    owner: 'Dueño',
    dr: 'DR',
    traffic: 'Traffic',
    sensitive: 'Temáticas',
    available: 'Disponible',
    instructions: '¿Cómo funciona?',
    step1: 'Pega URLs o sube un archivo Excel/CSV con dominios',
    step2: 'La herramienta limpia las URLs (deja solo el dominio)',
    step3: 'Busca cada dominio en tu catálogo de productos',
    step4: 'Muestra los encontrados y puedes descargar el Excel completo',
    noResults: 'No hay resultados',
    searchFirst: 'Realiza una búsqueda para ver los resultados',
  } : {
    title: 'Bulk Media Search',
    description: 'Search if domains exist in your catalog. Paste URLs or upload a file.',
    textTab: 'Paste Text',
    fileTab: 'Upload File',
    textPlaceholder: 'Paste URLs here (one per line):\n\nhttps://example.com\nwww.anotherdomain.com\nmydomain.com',
    search: 'Search',
    searching: 'Searching...',
    uploadFile: 'Upload Excel/CSV',
    selectFile: 'Select file...',
    results: 'Results',
    found: 'Found',
    notFound: 'Not Found',
    totalSearched: 'Total Searched',
    downloadExcel: 'Download Full Excel',
    domain: 'Domain',
    price: 'Purchase Price',
    pvp: 'Sale Price',
    owner: 'Owner',
    dr: 'DR',
    traffic: 'Traffic',
    sensitive: 'Topics',
    available: 'Available',
    instructions: 'How does it work?',
    step1: 'Paste URLs or upload an Excel/CSV file with domains',
    step2: 'The tool cleans URLs (keeps only the domain)',
    step3: 'Searches each domain in your product catalog',
    step4: 'Shows found items and you can download the full Excel',
    noResults: 'No results',
    searchFirst: 'Perform a search to see results',
  };

  // Search from text
  const handleTextSearch = async () => {
    if (!textInput.trim()) {
      toast.error(language === 'es' ? 'Introduce al menos una URL' : 'Enter at least one URL');
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.post(`${API}/media-search/search-text`, {
        text: textInput
      });
      setResults(response.data);
      toast.success(`${response.data.total_found} ${t.found}, ${response.data.total_not_found} ${t.notFound}`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
    }
  };

  // Search from file
  const handleFileSearch = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setSearching(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/media-search/search-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
      toast.success(`${response.data.total_found} ${t.found}, ${response.data.total_not_found} ${t.notFound}`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    if (results?.search_id) {
      window.open(`${API}/media-search/export/${results.search_id}`, '_blank');
    }
  };

  // Compare Excel
  const handleCompareExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setComparing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('owner_email', compareOwnerEmail);

      const response = await axios.post(`${API}/media-search/compare-excel?owner_email=${encodeURIComponent(compareOwnerEmail)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setCompareResults(response.data);
      setSelectedCompare(new Set());
      const s = response.data.summary;
      toast.success(
        language === 'es'
          ? `${s.cheaper} más baratos, ${s.more_expensive} más caros, ${s.only_in_provider} nuevos`
          : `${s.cheaper} cheaper, ${s.more_expensive} more expensive, ${s.only_in_provider} new`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la comparación');
    } finally {
      setComparing(false);
      if (compareFileRef.current) compareFileRef.current.value = '';
    }
  };

  const handleSelectAllCompare = (items) => {
    if (selectedCompare.size === items.length) {
      setSelectedCompare(new Set());
    } else {
      setSelectedCompare(new Set(items.map(i => i.domain)));
    }
  };

  const toggleCompareSelection = (domain) => {
    const ns = new Set(selectedCompare);
    if (ns.has(domain)) ns.delete(domain);
    else ns.add(domain);
    setSelectedCompare(ns);
  };

  const _executeApply = async (strategy, domainsOverride = null) => {
    // domainsOverride may be a SyntheticEvent when wired directly to onClick.
    // Only honor it when it's a real array.
    const domains = Array.isArray(domainsOverride)
      ? domainsOverride
      : Array.from(selectedCompare);
    if (domains.length === 0) {
      toast.error(language === 'es' ? 'Selecciona al menos un producto' : 'Select at least one product');
      return;
    }
    setApplyingCompare(true);
    try {
      const response = await axios.post(`${API}/media-search/compare-excel/apply`, {
        compare_id: compareResults.compare_id,
        selected_domains: domains,
        change_owner: changeOwner,
        sensitive_strategy: strategy || 'keep',
      });
      const updated = response.data?.updated ?? 0;
      if (updated === 0) {
        toast.error(
          language === 'es'
            ? 'No se actualizó ningún producto (¿están en tu catálogo?)'
            : 'No products updated (are they in your catalog?)'
        );
      } else {
        toast.success(
          language === 'es'
            ? `${updated} productos actualizados`
            : `${updated} products updated`
        );
      }
      // No auto-download: the user explicitly said the auto-open of the
      // Excel after apply was distracting. They can still download manually
      // from the "Descargar" button if needed.
      setSelectedCompare(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar cambios');
    } finally {
      setApplyingCompare(false);
      setSensitiveModal(null);
    }
  };

  const handleApplyCompare = async (domainsOverride = null) => {
    const domains = Array.isArray(domainsOverride)
      ? domainsOverride
      : Array.from(selectedCompare);
    if (domains.length === 0) {
      toast.error(language === 'es' ? 'Selecciona al menos un producto' : 'Select at least one product');
      return;
    }
    // ALWAYS show the strategy modal so the user explicitly decides what to
    // do with sensitive pricing AND whether the owner email changes. The
    // `conflicts` array highlights the rows that already have premium
    // pricing in the app — informational, not blocking.
    const domainsSet = new Set(domains);
    const conflicts = (compareResults?.cheaper || [])
      .filter((it) => domainsSet.has(it.domain) && it.has_sensitive_conflict)
      .map((it) => ({
        domain: it.domain,
        local_pcp: it.local_pcp,
        local_pp: it.local_pp,
        provider_pc: it.provider_pc,
      }));
    setSensitiveModal({ conflicts, domainsOverride: domains });
  };

  const handleDownloadCompare = () => {
    if (compareResults?.compare_id) {
      window.open(`${API}/media-search/compare-excel/export/${compareResults.compare_id}`, '_blank');
    }
  };

  const downloadBucket = async (bucket, label) => {
    if (!compareResults?.compare_id) {
      toast.error(language === 'es' ? 'No hay comparación activa' : 'No active comparison');
      return;
    }
    try {
      const response = await fetch(
        `${API}/media-search/compare-excel/export-bucket/${compareResults.compare_id}/${bucket}`
      );
      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const j = await response.json();
          detail = j.detail || detail;
        } catch (_) {}
        toast.error(detail);
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = `${bucket}_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(language === 'es' ? `Excel descargado: ${label}` : `Downloaded: ${label}`);
    } catch (err) {
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  const toggleDeleteSelection = (domain) => {
    const ns = new Set(selectedDelete);
    if (ns.has(domain)) ns.delete(domain);
    else ns.add(domain);
    setSelectedDelete(ns);
  };

  const handleDeleteProducts = async () => {
    if (selectedDelete.size === 0) return;
    setDeletingProducts(true);
    try {
      const response = await axios.post(`${API}/media-search/compare-excel/delete-products`, {
        domains: Array.from(selectedDelete),
      });
      toast.success(
        language === 'es'
          ? `${response.data.deleted} productos eliminados`
          : `${response.data.deleted} products deleted`
      );
      // Remove deleted from results
      if (compareResults) {
        const updated = { ...compareResults };
        updated.cheaper = updated.cheaper.filter(i => !selectedDelete.has(i.domain));
        updated.more_expensive = updated.more_expensive.filter(i => !selectedDelete.has(i.domain));
        updated.same_price = updated.same_price.filter(i => !selectedDelete.has(i.domain));
        setCompareResults(updated);
      }
      setSelectedDelete(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeletingProducts(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6" />
            {t.title}
          </h2>
          <p className="text-muted-foreground">{t.description}</p>
        </div>
      </div>

      {/* Instructions */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-blue-900">{t.instructions}</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>{t.step1}</li>
                <li>{t.step2}</li>
                <li>{t.step3}</li>
                <li>{t.step4}</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Options */}
      <Tabs defaultValue="text" className="w-full">
        <TabsList>
          <TabsTrigger value="text">{t.textTab}</TabsTrigger>
          <TabsTrigger value="file">{t.fileTab}</TabsTrigger>
          <TabsTrigger value="compare" className="flex items-center gap-1">
            <FileSpreadsheet className="h-4 w-4" />
            {language === 'es' ? 'Comparar Excel' : 'Compare Excel'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Textarea
                  placeholder={t.textPlaceholder}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                />
                <Button 
                  onClick={handleTextSearch} 
                  disabled={searching || !textInput.trim()}
                  className="w-full"
                >
                  {searching ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  {searching ? t.searching : t.search}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="file" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSearch}
                  className="hidden"
                  id="media-search-file"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={searching}
                  className="w-full h-24 border-dashed border-2"
                >
                  {searching ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 mb-2 animate-spin" />
                      <span>{t.searching}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                      <span>{t.selectFile}</span>
                      <span className="text-xs text-muted-foreground">Excel (.xlsx) o CSV</span>
                    </div>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Excel Tab */}
        <TabsContent value="compare" className="space-y-4">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileSpreadsheet className="h-5 w-5 text-amber-600" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-medium text-amber-900">
                    {language === 'es' ? '¿Cómo funciona?' : 'How does it work?'}
                  </h4>
                  <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
                    <li>{language === 'es' ? 'Sube el Excel del proveedor con columnas de dominio y precio' : 'Upload provider Excel with domain and price columns'}</li>
                    <li>{language === 'es' ? 'El sistema compara automáticamente contra tu catálogo' : 'System auto-compares against your catalog'}</li>
                    <li>{language === 'es' ? 'Muestra dónde puedes ahorrar, productos nuevos y más caros' : 'Shows savings, new products, and more expensive ones'}</li>
                    <li>{language === 'es' ? 'Selecciona los cambios y aplícalos directamente' : 'Select changes and apply directly'}</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>{language === 'es' ? 'Email del proveedor (opcional)' : 'Provider email (optional)'}</Label>
                <Input
                  value={compareOwnerEmail}
                  onChange={(e) => setCompareOwnerEmail(e.target.value)}
                  placeholder="proveedor@email.com"
                  className="mt-1"
                  data-testid="compare-owner-email"
                />
              </div>
              <input
                ref={compareFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleCompareExcel}
                className="hidden"
                id="compare-file"
              />
              <Button
                variant="outline"
                onClick={() => compareFileRef.current?.click()}
                disabled={comparing}
                className="w-full h-20 border-dashed border-2"
                data-testid="compare-upload-btn"
              >
                {comparing ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-6 w-6 mb-1 animate-spin" />
                    <span>{language === 'es' ? 'Comparando...' : 'Comparing...'}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-6 w-6 mb-1 text-muted-foreground" />
                    <span>{language === 'es' ? 'Subir Excel del proveedor' : 'Upload provider Excel'}</span>
                  </div>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Compare Results */}
          {compareResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="bg-slate-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{compareResults.summary.total_in_excel}</p>
                    <p className="text-xs text-muted-foreground">{language === 'es' ? 'En Excel' : 'In Excel'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{compareResults.summary.cheaper}</p>
                    <p className="text-xs text-green-600">{language === 'es' ? 'Más baratos' : 'Cheaper'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{compareResults.summary.more_expensive}</p>
                    <p className="text-xs text-red-600">{language === 'es' ? 'Más caros' : 'More expensive'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{compareResults.summary.same_price}</p>
                    <p className="text-xs text-blue-600">{language === 'es' ? 'Mismo precio' : 'Same price'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{compareResults.summary.only_in_provider}</p>
                    <p className="text-xs text-purple-600">{language === 'es' ? 'Nuevos' : 'New'}</p>
                  </CardContent>
                </Card>
              </div>

              {compareResults.summary.total_savings > 0 && (
                <Card className="bg-green-50 border-green-300">
                  <CardContent className="p-3 flex items-center justify-between">
                    <span className="text-green-800 font-medium">
                      {language === 'es' ? 'Ahorro potencial total:' : 'Total potential savings:'}
                    </span>
                    <span className="text-2xl font-bold text-green-600">€{compareResults.summary.total_savings}</span>
                  </CardContent>
                </Card>
              )}

              {/* Floating Action Bar */}
              <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg p-3 flex items-center justify-center gap-4 flex-wrap">
                <Button onClick={handleDownloadCompare} variant="outline" size="sm" data-testid="compare-download-btn">
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Descargar Excel' : 'Download Excel'}
                </Button>

                {/* "Apply ALL cheaper" + "Change owner" — always visible when
                    there are cheaper items, no manual row-selection required. */}
                {compareResults.cheaper?.length > 0 && (
                  <>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="change-owner-compare"
                        checked={changeOwner}
                        onCheckedChange={setChangeOwner}
                      />
                      <Label htmlFor="change-owner-compare" className="text-sm">
                        {language === 'es' ? 'Cambiar dueño' : 'Change owner'}
                      </Label>
                    </div>
                    <Button
                      onClick={() => {
                        const allDomains = compareResults.cheaper
                          .filter(i => !selectedDelete.has(i.domain))
                          .map(i => i.domain);
                        handleApplyCompare(allDomains);
                      }}
                      disabled={applyingCompare}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      size="sm"
                      data-testid="compare-apply-all-cheaper-btn"
                    >
                      {applyingCompare ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      {language === 'es'
                        ? `Aplicar TODOS los más baratos (${compareResults.cheaper.filter(i => !selectedDelete.has(i.domain)).length})`
                        : `Apply ALL cheaper (${compareResults.cheaper.filter(i => !selectedDelete.has(i.domain)).length})`}
                    </Button>
                  </>
                )}

                {/* "Apply N selected" — only when the user manually ticked rows. */}
                {selectedCompare.size > 0 && (
                  <Button
                    onClick={handleApplyCompare}
                    disabled={applyingCompare}
                    className="bg-green-600 hover:bg-green-700"
                    size="sm"
                    data-testid="compare-apply-btn"
                  >
                    {applyingCompare ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {language === 'es' ? `Aplicar ${selectedCompare.size} seleccionados` : `Apply ${selectedCompare.size} selected`}
                  </Button>
                )}
                {selectedDelete.size > 0 && (
                  <Button
                    onClick={handleDeleteProducts}
                    disabled={deletingProducts}
                    variant="destructive"
                    size="sm"
                    data-testid="compare-delete-btn"
                  >
                    {deletingProducts ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    {language === 'es' ? `Eliminar ${selectedDelete.size}` : `Delete ${selectedDelete.size}`}
                  </Button>
                )}
              </div>

              {/* Cheaper Table */}
              {compareResults.cheaper?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle className="text-green-600 flex items-center gap-2 text-base">
                        <TrendingDown className="h-5 w-5" />
                        {language === 'es' ? `Más baratos en proveedor (${compareResults.cheaper.length})` : `Cheaper from provider (${compareResults.cheaper.length})`}
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        onClick={() => downloadBucket('cheaper', language === 'es' ? 'Más Baratos' : 'Cheaper')}
                        data-testid="compare-download-cheaper-btn"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Descargar Más Baratos' : 'Download Cheaper'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="border rounded-md overflow-auto" style={{ maxHeight: '500px' }}>
                      <Table>
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead className="w-10">
                              <Checkbox
                                checked={selectedCompare.size === compareResults.cheaper.length && compareResults.cheaper.length > 0}
                                onCheckedChange={() => handleSelectAllCompare(compareResults.cheaper)}
                              />
                            </TableHead>
                            <TableHead>{language === 'es' ? 'Dominio' : 'Domain'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'Tu PC' : 'Your PC'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PCProvee' : 'Prov. PC'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'Ahorro PC' : 'PC Savings'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PCsensible' : 'Sensitive PC'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PCsensible Prov.' : 'Prov. Sens. PC'}</TableHead>
                            <TableHead className="text-right">PVP</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PVPsensible' : 'Sensitive PVP'}</TableHead>
                            <TableHead>{language === 'es' ? 'Email App' : 'App Email'}</TableHead>
                            <TableHead>{language === 'es' ? 'Email Prov.' : 'Prov. Email'}</TableHead>
                            <TableHead className="w-10 text-center">
                              <Trash2 className="h-4 w-4 mx-auto text-red-400" />
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {compareResults.cheaper.map((item, idx) => (
                            <TableRow key={idx} className={`hover:bg-green-50/50 ${selectedDelete.has(item.domain) ? 'bg-red-50/50 opacity-60 line-through' : ''}`}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedCompare.has(item.domain)}
                                  onCheckedChange={() => toggleCompareSelection(item.domain)}
                                  disabled={selectedDelete.has(item.domain)}
                                />
                              </TableCell>
                              <TableCell className="font-medium text-sm">{item.domain}</TableCell>
                              <TableCell className="text-right">€{item.local_pc || 0}</TableCell>
                              <TableCell className="text-right text-green-600 font-medium">€{item.provider_pc || 0}</TableCell>
                              <TableCell className="text-right">
                                {item.difference > 0 ? (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                    -€{item.difference}
                                  </Badge>
                                ) : <span className="text-muted-foreground">-</span>}
                              </TableCell>
                              <TableCell className="text-right">€{item.local_pcp || 0}</TableCell>
                              <TableCell className="text-right text-green-600 font-medium">€{item.provider_pcp || 0}</TableCell>
                              <TableCell className="text-right">€{item.new_pvp || 0}</TableCell>
                              <TableCell className="text-right">€{item.provider_pp || 0}</TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={item.local_email || ''}>{item.local_email || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={item.provider_email || ''}>{item.provider_email || '-'}</TableCell>
                              <TableCell className="text-center">
                                <Checkbox
                                  checked={selectedDelete.has(item.domain)}
                                  onCheckedChange={() => toggleDeleteSelection(item.domain)}
                                  className="border-red-300 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* More Expensive Table */}
              {compareResults.more_expensive?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle className="text-red-600 flex items-center gap-2 text-base">
                        <TrendingUp className="h-5 w-5" />
                        {language === 'es' ? `Más caros en proveedor (${compareResults.more_expensive.length})` : `More expensive from provider (${compareResults.more_expensive.length})`}
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => downloadBucket('more_expensive', language === 'es' ? 'Más Caros' : 'Expensive')}
                        data-testid="compare-download-expensive-btn"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Descargar Más Caros' : 'Download Expensive'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="border rounded-md overflow-auto" style={{ maxHeight: '250px' }}>
                      <Table>
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead>{language === 'es' ? 'Dominio' : 'Domain'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'Tu PC' : 'Your PC'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PCProvee' : 'Prov. PC'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'Dif. PC' : 'PC Diff'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PCsensible' : 'Sensitive PC'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'PCsensible Prov.' : 'Prov. Sens. PC'}</TableHead>
                            <TableHead>{language === 'es' ? 'Email App' : 'App Email'}</TableHead>
                            <TableHead>{language === 'es' ? 'Email Prov.' : 'Prov. Email'}</TableHead>
                            <TableHead className="w-10 text-center">
                              <Trash2 className="h-4 w-4 mx-auto text-red-400" />
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {compareResults.more_expensive.map((item, idx) => (
                            <TableRow key={idx} className={selectedDelete.has(item.domain) ? 'bg-red-50/50 opacity-60 line-through' : ''}>
                              <TableCell className="font-medium text-sm">{item.domain}</TableCell>
                              <TableCell className="text-right">€{item.local_pc || 0}</TableCell>
                              <TableCell className="text-right text-red-600 font-medium">€{item.provider_pc || 0}</TableCell>
                              <TableCell className="text-right">
                                {item.difference !== 0 ? (
                                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                    +€{Math.abs(item.difference)}
                                  </Badge>
                                ) : <span className="text-muted-foreground">-</span>}
                              </TableCell>
                              <TableCell className="text-right">€{item.local_pcp || 0}</TableCell>
                              <TableCell className="text-right text-red-600 font-medium">€{item.provider_pcp || 0}</TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={item.local_email || ''}>{item.local_email || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={item.provider_email || ''}>{item.provider_email || '-'}</TableCell>
                              <TableCell className="text-center">
                                <Checkbox
                                  checked={selectedDelete.has(item.domain)}
                                  onCheckedChange={() => toggleDeleteSelection(item.domain)}
                                  className="border-red-300 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* New products (only in provider) */}
              {compareResults.only_in_provider?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-purple-600 flex items-center gap-2 text-base">
                      <AlertTriangle className="h-5 w-5" />
                      {language === 'es' ? `Nuevos — solo en proveedor (${compareResults.total_only_in_provider || compareResults.only_in_provider.length})` : `New — only in provider (${compareResults.total_only_in_provider || compareResults.only_in_provider.length})`}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        onClick={() => downloadBucket('only_in_provider', language === 'es' ? 'Nuevos' : 'New')}
                        data-testid="compare-download-new-btn"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Descargar Nuevos (Excel importable)' : 'Download New (importable Excel)'}
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {compareResults.only_in_provider.slice(0, 60).map((item, idx) => (
                        <Badge key={idx} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          {item.domain} {item.provider_price > 0 && `(€${item.provider_price})`}
                        </Badge>
                      ))}
                      {compareResults.only_in_provider.length > 60 && (
                        <Badge variant="outline">+{compareResults.only_in_provider.length - 60} más</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{results.total_searched}</p>
                <p className="text-sm text-muted-foreground">{t.totalSearched}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{results.total_found}</p>
                <p className="text-sm text-green-600">{t.found}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{results.total_not_found}</p>
                <p className="text-sm text-red-600">{t.notFound}</p>
              </CardContent>
            </Card>
          </div>

          {/* Download Button */}
          <div className="flex justify-end">
            <Button onClick={handleDownloadExcel} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              {t.downloadExcel}
            </Button>
          </div>

          {/* Found Items Table */}
          {results.found?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-green-600 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  {t.found} ({results.found.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-auto" style={{ maxHeight: '500px' }}>
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-100 z-10">
                      <tr className="border-b">
                        <th className="h-10 px-4 text-left font-semibold">#</th>
                        <th className="h-10 px-4 text-left font-semibold">{t.domain}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.price}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.pvp}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.dr}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.traffic}</th>
                        <th className="h-10 px-4 text-left font-semibold">{t.sensitive}</th>
                        <th className="h-10 px-4 text-left font-semibold">{t.owner}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.found.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-4 text-muted-foreground font-mono text-xs">{idx + 1}</td>
                          <td className="p-4 font-medium">{item.domain}</td>
                          <td className="p-4 text-right">€{item.price || 0}</td>
                          <td className="p-4 text-right">€{item.pvp || 0}</td>
                          <td className="p-4 text-right">{item.dr || '-'}</td>
                          <td className="p-4 text-right">{item.traffic?.toLocaleString() || '-'}</td>
                          <td className="p-4">
                            {item.sensitive_topics?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {item.sensitive_topics.slice(0, 3).map((topic, i) => (
                                  <Badge key={i} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                                    {topic}
                                  </Badge>
                                ))}
                                {item.sensitive_topics.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{item.sensitive_topics.length - 3}</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-4 text-muted-foreground">{item.owner || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Not Found List */}
          {results.not_found?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  {t.notFound} ({results.total_not_found})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {results.not_found.slice(0, 50).map((domain, idx) => (
                    <Badge key={idx} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      {domain}
                    </Badge>
                  ))}
                  {results.not_found.length > 50 && (
                    <Badge variant="outline">+{results.not_found.length - 50} más</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!results && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t.noResults}</p>
            <p className="text-muted-foreground">{t.searchFirst}</p>
          </CardContent>
        </Card>
      )}

      {/* Strategy decision modal — shown ALWAYS before apply so the user
          explicitly picks how to handle sensitive prices + owner email. */}
      {sensitiveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSensitiveModal(null); }}
          data-testid="sensitive-conflict-modal"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-5 border-b">
              <h3 className="text-lg font-semibold text-slate-800">
                {language === 'es'
                  ? `Confirmar cambios en ${sensitiveModal.domainsOverride?.length || 0} producto(s)`
                  : `Confirm changes for ${sensitiveModal.domainsOverride?.length || 0} product(s)`}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                {sensitiveModal.conflicts.length > 0 ? (
                  language === 'es'
                    ? <>El Excel del proveedor <strong>solo trae precio regular</strong> para <strong>{sensitiveModal.conflicts.length}</strong> medio(s), pero estos ya tienen precios de temática sensible en tu app. Elige qué hacer con los precios sensibles:</>
                    : <>The provider Excel <strong>only has regular PC</strong> for <strong>{sensitiveModal.conflicts.length}</strong> media that already have sensitive pricing in your app. Pick what to do with sensitive prices:</>
                ) : (
                  language === 'es'
                    ? 'No hay conflictos de temática sensible detectados. Elige la estrategia general para los precios sensibles:'
                    : 'No sensitive pricing conflicts detected. Pick the general strategy for sensitive prices:'
                )}
              </p>
              <p className="text-xs text-slate-600 mt-2">
                {language === 'es' ? 'Email del editor: ' : 'Owner email: '}
                <strong>{changeOwner
                  ? (language === 'es' ? 'SE REEMPLAZARÁ con el del proveedor' : 'WILL BE REPLACED with provider email')
                  : (language === 'es' ? 'NO se cambiará' : 'will NOT be changed')}</strong>
                {' · '}
                <button
                  type="button"
                  onClick={() => setChangeOwner(!changeOwner)}
                  className="underline text-blue-600 hover:text-blue-800"
                >
                  {language === 'es' ? 'cambiar' : 'toggle'}
                </button>
              </p>
            </div>

            {sensitiveModal.conflicts.length > 0 && (
              <div className="p-5 space-y-2 max-h-[35vh] overflow-y-auto bg-amber-50/40">
                <div className="text-xs font-semibold text-amber-800 mb-2">
                  {language === 'es' ? 'Medios con precio sensible en tu app:' : 'Media with sensitive pricing in your app:'}
                </div>
                {sensitiveModal.conflicts.slice(0, 20).map((c) => (
                  <div key={c.domain} className="text-xs border-b pb-1.5 last:border-0">
                    <span className="font-mono text-slate-700">{c.domain}</span>
                    <span className="text-muted-foreground ml-2">
                      {language === 'es' ? 'PC sensible actual:' : 'current sensitive PC:'} €{c.local_pcp} ·{' '}
                      {language === 'es' ? 'PVP sensible actual:' : 'current sensitive PVP:'} €{c.local_pp}
                    </span>
                  </div>
                ))}
                {sensitiveModal.conflicts.length > 20 && (
                  <div className="text-xs text-muted-foreground italic">
                    ...{language === 'es' ? `y ${sensitiveModal.conflicts.length - 20} más` : `and ${sensitiveModal.conflicts.length - 20} more`}
                  </div>
                )}
              </div>
            )}

            <div className="p-5 border-t bg-slate-50 space-y-2">
              <Button
                onClick={() => _executeApply('keep', sensitiveModal.domainsOverride)}
                disabled={applyingCompare}
                className="w-full justify-start bg-blue-600 hover:bg-blue-700"
                data-testid="sensitive-keep-btn"
              >
                <strong className="mr-2">A)</strong>
                {language === 'es'
                  ? 'Mantener los precios sensibles actuales (recomendado)'
                  : 'Keep current sensitive prices (recommended)'}
              </Button>
              <Button
                onClick={() => _executeApply('clear', sensitiveModal.domainsOverride)}
                disabled={applyingCompare}
                variant="destructive"
                className="w-full justify-start"
                data-testid="sensitive-clear-btn"
              >
                <strong className="mr-2">B)</strong>
                {language === 'es'
                  ? 'Borrar precios sensibles (este proveedor no admite temática sensible)'
                  : 'Clear sensitive prices (this provider does not accept sensitive topics)'}
              </Button>
              <Button
                onClick={() => _executeApply('mirror', sensitiveModal.domainsOverride)}
                disabled={applyingCompare}
                variant="secondary"
                className="w-full justify-start"
                data-testid="sensitive-mirror-btn"
              >
                <strong className="mr-2">C)</strong>
                {language === 'es'
                  ? 'Igualar al precio regular del proveedor (sin sobrecoste)'
                  : 'Mirror provider regular price (no premium markup)'}
              </Button>
              <Button
                onClick={() => _executeApply('sync', sensitiveModal.domainsOverride)}
                disabled={applyingCompare}
                className="w-full justify-start bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="sensitive-sync-btn"
              >
                <strong className="mr-2">D)</strong>
                {language === 'es'
                  ? 'Sincronizar temáticas sensibles con el proveedor (reemplaza flags + precios)'
                  : 'Sync sensitive topics with provider (replace flags + prices)'}
              </Button>
              <Button
                onClick={() => setSensitiveModal(null)}
                variant="outline"
                className="w-full"
                disabled={applyingCompare}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaSearchTab;
